import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import ProductInquiryModal from '../components/ProductInquiryModal';
import ProductDetailCard from '../components/products/ProductDetailCard';
import { useLocalizedProducts } from '../hooks/useLocalizedCMS';
import { trackPixel } from '../lib/pixel';

export default function ProductsPage() {
  const { t } = useLanguage();
  const location = useLocation();
  const [modalProduct, setModalProduct] = useState(null);
  const { products } = useLocalizedProducts();

  // Fire ViewContent on Products page (Meta Pixel — helps Meta Ads optimize)
  useEffect(() => {
    trackPixel('ViewContent', { content_type: 'product_group', content_name: 'ARV Products Catalog' });
  }, []);

  // Scroll to anchored product after page renders
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const tryScroll = (attempts = 0) => {
        const el = document.getElementById(id);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        } else if (attempts < 10) {
          setTimeout(() => tryScroll(attempts + 1), 150);
        }
      };
      tryScroll();
    }
  }, [location.hash]);

  return (
    <main>
      <SEO
        title="Products"
        path="/products"
        description="Explore ARV Machinery products: Rotavator (3–10 ft, 30–72 EN steel blades), Disc Harrow, Haloom Mulcher (3-row, 24–30 inch), Cultivator and Mini Rotavator. Built for Punjab farms."
        keywords="ARV Rotavator, Disc Harrow, Haloom Mulcher, Cultivator, Mini Rotavator, tractor implements Punjab"
      />
      {/* Hero */}
      <section className="py-16 text-center" style={{ backgroundImage: 'linear-gradient(135deg, #0A3F22 0%, #1B6B3D 100%)' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-2xl px-4"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: '#FCD34D' }}>{t.products.sectionBadge}</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mt-2 leading-tight">
            {t.products.title} <span style={{ color: '#FCD34D' }}>{t.products.title2}</span>
          </h1>
          <p className="mt-4 text-white/70 text-base">{t.products.subtitle}</p>
        </motion.div>
      </section>

      {/* Products Grid */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-12">
          {products.map((product, i) => (
            <ProductDetailCard
              key={product.id}
              product={product}
              index={i}
              enquireLabel={t.products.enquire}
              onEnquire={setModalProduct}
            />
          ))}
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-12" style={{ backgroundColor: '#0A3F22' }}>
        <div className="mx-auto max-w-2xl px-4 text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-2">Need a custom specification?</h2>
          <p className="text-white/70 text-sm mb-6">Our engineers are happy to discuss custom requirements for your farm or fleet.</p>
          <button
            data-testid="products-page-cta"
            onClick={() => setModalProduct({ name: 'Custom Specification' })}
            className="px-8 py-3 rounded-lg font-heading font-semibold text-white transition-all hover:scale-105"
            style={{ backgroundColor: '#D97706' }}
          >
            Talk to an Engineer →
          </button>
        </div>
      </section>

      {modalProduct && (
        <ProductInquiryModal product={modalProduct} onClose={() => setModalProduct(null)} />
      )}
    </main>
  );
}
